'use client'

/**
 * TwoFactorOnboardingModal — proaktiver 3-Schritt-Wizard nach erstem Login.
 *
 * Step 0 (Intro):    Erklärt KONKRET warum 2FA — Credential-Stuffing-Risiko,
 *                    Wertungs-/Donator-Schutz. Buttons: "Jetzt einrichten" /
 *                    "Später erinnern".
 * Step 1 (App):      App-Store-Links Android + iOS für Google Authenticator,
 *                    Microsoft Authenticator, 2FAS. Plakativ groß.
 * Step 2 (QR):       QR-Code anzeigen + 6-Digit-Code-Input + Backup-Codes
 *                    notieren.
 *
 * "Später erinnern" setzt einen localStorage-Flag mit 7-Tage-Snooze. Banner
 * im Settings bleibt dauerhaft.
 *
 * Admin-User mit role=ADMIN können nur "Jetzt einrichten" — kein Snooze, weil
 * Admin-Pflicht durch Middleware durchgesetzt wird.
 */

import { useEffect, useState } from 'react'
import {
  Shield, ShieldCheck, Smartphone, QrCode, KeyRound, AlertTriangle,
  Apple, Loader2, Copy, Check, ExternalLink, X,
} from 'lucide-react'

const SNOOZE_KEY = 'fgl_2fa_snooze_until'
const SNOOZE_DAYS = 7
// Pro Browser-Tab merken, ob in dieser Session schon weggeklickt — verhindert
// dass das Modal bei jeder Page-Navigation oder Reload erneut aufpoppt.
const SESSION_DISMISS_KEY = 'fgl_2fa_dismissed_this_session'

export interface TwoFactorOnboardingModalProps {
  open: boolean
  onClose: () => void
  /** Wenn true (z.B. Admin nach Login): kein Snooze möglich, kein Schließen ohne Setup. */
  forceSetup?: boolean
  /** Callback nach erfolgreichem Setup. Page kann reagieren (Reload, Banner ausblenden). */
  onSuccess?: () => void
  /** Vorname für persönliche Ansprache, optional. */
  firstName?: string | null
}

type Step = 'intro' | 'app' | 'qr'

interface SetupData {
  qrCode: string         // data:image/png base64
  secret: string         // ASCII secret als Backup-Eingabe
  backupCodes: string[]  // 10 Codes
}

export function TwoFactorOnboardingModal({
  open, onClose, forceSetup = false, onSuccess, firstName,
}: TwoFactorOnboardingModalProps) {
  const [step, setStep] = useState<Step>('intro')
  const [password, setPassword] = useState('')
  const [setupData, setSetupData] = useState<SetupData | null>(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [backupConfirmed, setBackupConfirmed] = useState(false)
  const [copiedSecret, setCopiedSecret] = useState(false)

  // Reset bei Öffnen
  useEffect(() => {
    if (open) {
      setStep('intro')
      setPassword('')
      setSetupData(null)
      setVerifyCode('')
      setError(null)
      setSuccess(false)
      setBackupConfirmed(false)
    }
  }, [open])

  if (!open) return null

  // ── Snooze (für Nicht-Admins, 7 Tage) ──
  function snoozeForLater() {
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000
    try { localStorage.setItem(SNOOZE_KEY, String(until)) } catch {}
    handleDismiss()
  }

  // ── Schliesst das Modal — sessionStorage-Flag pro Tab + 7-Tage-Snooze
  // 2026-05-15 (Bugfix): Vorher hat der X-Button NUR sessionStorage gesetzt,
  // sodass das Modal bei jedem Page-Reload bzw. neuen Tab wieder aufpoppte.
  // Tester haben sich beschwert dass es nach dem Wegklicken immer wieder kam.
  // Jetzt: Schließen ist äquivalent zu „Später" — 7 Tage Ruhe.
  // Admin wird per forceSetup=true erkannt: dann KEIN Snooze (Middleware
  // sperrt /admin/* eh und das Modal soll bei der nächsten Navigation wieder
  // auftauchen damit der Admin 2FA tatsächlich einrichtet).
  function handleDismiss() {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, '1') } catch {}
    if (!forceSetup) {
      try {
        const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000
        localStorage.setItem(SNOOZE_KEY, String(until))
      } catch {}
    }
    onClose()
  }

  // ── Step 1 → 2: Setup-Secret holen ──
  async function startSetup() {
    if (!password) {
      setError('Bitte dein Passwort eingeben — wir verschlüsseln den 2FA-Schlüssel damit.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/2fa/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Setup fehlgeschlagen')
        return
      }
      setSetupData({
        qrCode: data.qrCodeDataUrl ?? data.qrCode,
        secret: data.secret,
        backupCodes: data.backupCodes,
      })
      setStep('qr')
    } catch (e: any) {
      setError(e?.message || 'Netzwerkfehler')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2: Code verifizieren + 2FA aktivieren ──
  async function confirmCode() {
    if (verifyCode.length !== 6) {
      setError('Bitte den 6-stelligen Code aus deiner App eingeben.')
      return
    }
    if (!backupConfirmed) {
      setError('Bitte bestätige, dass du die Backup-Codes notiert hast.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/2fa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verifyCode }),
      })
      const data = await res.json()
      if (!res.ok || !data.success) {
        setError(data.error || 'Code stimmt nicht — versuch nochmal.')
        return
      }
      setSuccess(true)
      try { localStorage.removeItem(SNOOZE_KEY) } catch {}
      onSuccess?.()
    } catch (e: any) {
      setError(e?.message || 'Netzwerkfehler')
    } finally {
      setLoading(false)
    }
  }

  function copySecret() {
    if (!setupData?.secret) return
    navigator.clipboard?.writeText(setupData.secret).catch(() => {})
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 1500)
  }

  function downloadBackupCodes() {
    if (!setupData?.backupCodes) return
    const text = [
      'FREE GLIDE LEAGUE — Backup-Codes für 2-Faktor-Authentifizierung',
      '',
      `Erzeugt am: ${new Date().toLocaleString('de-DE')}`,
      `Account: ${firstName ?? 'dein Account'}`,
      '',
      'Jeder Code ist nur EINMAL verwendbar. Bewahre sie an einem sicheren Ort auf',
      '(Passwort-Manager, Tresor, Drucker → Versteck — NICHT auf demselben Gerät',
      'wo deine Authenticator-App läuft).',
      '',
      'Wenn du dein Smartphone verlierst, kannst du dich mit einem dieser Codes',
      'einmalig einloggen und 2FA neu einrichten.',
      '',
      ...setupData.backupCodes.map(c => `  ${c}`),
      '',
      '— FREE GLIDE LEAGUE Plattform',
    ].join('\n')
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `fgl-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Render ──
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => { if (!loading) handleDismiss() }}
      />
      <div className={`relative z-10 w-full ${step === 'intro' ? 'max-w-md' : 'max-w-2xl'} max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-[var(--accent)]/40 bg-[var(--bg-card)] shadow-2xl`}>
        {/* Close-Button — auch für Admins; Admin-Pflicht wird via Middleware
            durchgesetzt (Redirect von /admin/* zurück nach /app). So bleibt
            das Modal nicht-blockierend für /app/* Pages */}
        {!loading && (
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label="Schließen"
          >
            <X className="h-5 w-5 text-[var(--muted)]" />
          </button>
        )}

        {/* ── Erfolgs-Screen ── */}
        {success ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-green-500/15 border-2 border-green-500/40 flex items-center justify-center mb-4">
              <ShieldCheck className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-black mb-2">Geschafft — dein Account ist jetzt geschützt!</h2>
            <p className="text-[var(--muted)] mb-6 leading-relaxed">
              Beim nächsten Login fragt die FGL-Plattform nach dem 6-stelligen Code aus deiner Authenticator-App.
              Selbst wenn dein Passwort irgendwo geleakt sein sollte, kommt niemand rein.
            </p>
            <button onClick={onClose} className="btn-primary !px-8">
              Weiter zur FGL-Plattform
            </button>
          </div>
        ) : step === 'intro' ? (
          // ─── STEP 0: Intro — kompakter Hinweis (2026-05-15) ───
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/40 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-[var(--accent)]" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-black tracking-tight leading-tight">
                  {firstName ? `${firstName}, ` : ''}2FA aktivieren?
                </h2>
                <p className="text-[11px] text-[var(--muted)] mt-0.5">
                  Empfohlen — schützt deinen Account vor Credential-Stuffing.
                </p>
              </div>
            </div>

            <p className="text-[12px] text-[var(--muted)] leading-relaxed mb-4">
              Beim Login zusätzlich 6-stelliger Code aus deiner Authenticator-App.
              Dauert 60 Sekunden zum Einrichten — falls dein Passwort jemals leakt, kommt trotzdem niemand rein.
            </p>

            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={() => setStep('app')}
                className="btn-primary !py-2.5 !text-[13px] flex items-center justify-center gap-1.5 font-bold"
              >
                <Shield className="h-4 w-4" />
                <span>Einrichten</span>
              </button>
              <button
                onClick={forceSetup ? handleDismiss : snoozeForLater}
                className="btn-secondary !py-2.5 !text-[13px]"
              >
                {forceSetup ? 'Schließen' : 'Später'}
              </button>
            </div>
            <p className="text-[10px] text-[var(--muted)] mt-2 text-center">
              {forceSetup
                ? 'Für /admin später nötig.'
                : '„Später" pausiert 7 Tage. Jederzeit über Einstellungen → Sicherheit nachholen.'}
            </p>
          </div>
        ) : step === 'app' ? (
          // ─── STEP 1: App installieren ───
          <div className="p-6 sm:p-8">
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-[var(--accent)]/15 flex items-center justify-center font-black text-[var(--accent)]">1</div>
                <h2 className="text-xl font-black">Authenticator-App installieren</h2>
              </div>
              <p className="text-sm text-[var(--muted)] ml-12">
                Eine kleine kostenlose App auf deinem Smartphone — generiert alle 30 Sek einen neuen 6-stelligen Code.
                Wenn du schon eine hast (Banking, andere Plattformen), kannst du dieselbe nutzen.
              </p>
            </div>

            <div className="space-y-3 mb-5">
              <AppRow
                title="Google Authenticator"
                badge="empfohlen — einfachste Wahl"
                androidUrl="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                iosUrl="https://apps.apple.com/app/google-authenticator/id388497605"
                description="Von Google, kein Konto nötig. Funktioniert offline."
              />
              <AppRow
                title="Microsoft Authenticator"
                badge="auch sehr gut"
                androidUrl="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                iosUrl="https://apps.apple.com/app/microsoft-authenticator/id983156458"
                description="Von Microsoft. Wenn du eh ein Microsoft-Konto hast — gleich nehmen."
              />
              <AppRow
                title="2FAS (Open Source)"
                badge="Open Source"
                androidUrl="https://play.google.com/store/apps/details?id=com.twofasapp"
                iosUrl="https://apps.apple.com/app/2fa-authenticator-2fas/id1217793794"
                description="Open-Source-Alternative — komplett transparent und werbefrei."
              />
            </div>

            <div className="rounded-xl bg-[var(--accent)]/8 border border-[var(--accent)]/30 p-4 mb-5">
              <p className="text-sm">
                <strong className="text-[var(--accent)]">App installiert?</strong> {' '}
                Mach sie noch nicht auf — wir öffnen sie gleich im nächsten Schritt zusammen mit dem QR-Code.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[var(--muted)] mb-1.5">
                Dein FGL-Login-Passwort eingeben
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') startSetup() }}
                placeholder="Das Passwort, mit dem du dich einloggst"
                className="input w-full"
                /* R100.63: autoFocus entfernt für mobile-friendly UX */
              />
              <p className="text-xs text-[var(--muted)] mt-1.5">
                Das ist <strong>nicht</strong> der 6-stellige 2FA-Code — den richten wir gleich gemeinsam ein.
                Wir brauchen dein Login-Passwort hier, weil wir damit deinen 2FA-Schlüssel verschlüsseln, sodass ihn niemand außer dir lesen kann.
              </p>
            </div>

            {error && (
              <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="mt-5 flex gap-3">
              <button onClick={() => setStep('intro')} className="btn-secondary">
                Zurück
              </button>
              <button onClick={startSetup} disabled={loading || !password} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                QR-Code anzeigen →
              </button>
            </div>
          </div>
        ) : step === 'qr' && setupData ? (
          // ─── STEP 2: QR + Verify + Backup-Codes ───
          <div className="p-6 sm:p-8">
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-[var(--accent)]/15 flex items-center justify-center font-black text-[var(--accent)]">2</div>
                <h2 className="text-xl font-black">QR-Code scannen + Code eingeben</h2>
              </div>
              <p className="text-sm text-[var(--muted)] ml-12">
                Öffne deine Authenticator-App, tippe „+" oder „Code hinzufügen" und scann den Code unten.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-xl shadow-lg">
                  <img src={setupData.qrCode} alt="QR-Code" className="w-48 h-48 sm:w-56 sm:h-56" />
                </div>
                <button
                  onClick={copySecret}
                  className="mt-3 text-xs flex items-center gap-1.5 text-[var(--muted)] hover:text-[var(--accent)]"
                >
                  {copiedSecret ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  {copiedSecret ? 'kopiert!' : 'Schlüssel manuell eingeben'}
                </button>
                {copiedSecret && (
                  <code className="mt-2 text-[10px] font-mono text-[var(--muted)] break-all">
                    {setupData.secret}
                  </code>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-[var(--muted)] mb-1.5">
                  Code aus der App eingeben
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={e => { if (e.key === 'Enter') confirmCode() }}
                  placeholder="000000"
                  className="input w-full !text-2xl text-center font-mono tracking-[0.4em] !py-4"
                  /* R106.4.8: autoFocus raus — Mobile-Tastatur-Trigger */
                />
                <p className="text-xs text-[var(--muted)] mt-1.5">
                  Der Code wechselt alle 30 Sekunden — keine Sorge, FGL akzeptiert auch den vorherigen.
                </p>

                <div className="mt-5">
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-400 mb-2">
                    🆘 Backup-Codes — JETZT speichern!
                  </p>
                  <div className="rounded-lg bg-amber-500/8 border border-amber-500/30 p-3">
                    <div className="grid grid-cols-2 gap-1.5 font-mono text-xs mb-3">
                      {setupData.backupCodes.map((code, i) => (
                        <div key={i} className="bg-[var(--bg-primary)] rounded px-2 py-1 text-center">{code}</div>
                      ))}
                    </div>
                    <button
                      onClick={downloadBackupCodes}
                      className="w-full text-xs py-2 rounded-md border border-amber-500/40 hover:bg-amber-500/15 text-amber-400 font-bold flex items-center justify-center gap-1.5"
                    >
                      <KeyRound className="h-3.5 w-3.5" /> Als TXT-Datei speichern
                    </button>
                    <p className="text-[10px] text-[var(--muted)] mt-2 leading-snug">
                      Wenn du dein Smartphone verlierst, brauchst du einen dieser Codes um wieder reinzukommen.
                      Druck sie aus, leg sie in deinen Passwort-Manager — aber <strong>nicht</strong> auf dem
                      Smartphone speichern.
                    </p>
                  </div>
                </div>

                <label className="mt-4 flex items-start gap-2 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={backupConfirmed}
                    onChange={e => setBackupConfirmed(e.target.checked)}
                    className="mt-0.5 accent-[var(--accent)]"
                  />
                  <span className="text-[var(--muted)]">
                    Ich habe die Backup-Codes notiert oder als Datei gespeichert.
                  </span>
                </label>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400 mb-3">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep('app')} className="btn-secondary">
                Zurück
              </button>
              <button
                onClick={confirmCode}
                disabled={loading || verifyCode.length !== 6 || !backupConfirmed}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                2FA aktivieren
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function AppRow({ title, badge, androidUrl, iosUrl, description }: {
  title: string
  badge?: string
  androidUrl: string
  iosUrl: string
  description: string
}) {
  return (
    <div className="rounded-xl border border-[var(--border)] hover:border-[var(--accent)]/40 transition-colors p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Smartphone className="h-5 w-5 text-[var(--accent)] flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-[var(--text)] truncate">{title}</p>
              {badge && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/30">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--muted)]">{description}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <a
            href={androidUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-[var(--accent)]/10 flex items-center gap-1.5"
          >
            <span className="font-bold">Android</span> <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href={iosUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-[var(--accent)]/10 flex items-center gap-1.5"
          >
            <Apple className="h-3 w-3" /> <span className="font-bold">iOS</span>
          </a>
        </div>
      </div>
    </div>
  )
}

/**
 * Helper-Hook: Soll das Modal jetzt gezeigt werden?
 *
 * Logik (R89.2 2026-05-18 — Granaten-Bugfix Reload/Back-Button):
 *  - User hat 2FA aktiv → nein
 *  - SessionDismiss-Flag gesetzt → nein (auch fuer Admin! Modal-Spam-Stop)
 *  - URL-Param `?require2fa=1` (Middleware-Redirect nach /admin-Versuch ohne
 *    2FA): SessionDismiss + Snooze gecleart, Modal zwingend zeigen
 *  - Admin: zeigen (force=true)
 *  - Nicht-Admin: zeigen wenn Snooze abgelaufen, sonst nein
 *
 * Vorher-Bug: Admin-Pfad ignorierte sessionStorage komplett -> Modal poppte
 * bei jedem Reload, Back-Button, BFCache-Restore wieder auf. Tester konnten
 * die App kaum nutzen.
 *
 * BFCache-Workaround: pageshow-Event mit persisted=true ist BFCache-Restore.
 * Dann State neu berechnen — sonst zeigt das Modal evtl. trotz Dismiss aus
 * gecachten React-State, oder bleibt zu obwohl Status sich aenderte.
 */
export function useTwoFactorOnboardingTrigger(
  twoFactorEnabled: boolean | null,
  isAdmin: boolean,
): { show: boolean; force: boolean } {
  const [show, setShow] = useState(false)

  useEffect(() => {
    function recompute() {
      // 2026-06-18 abends (Stephan-Spec, Punkt 7): solange Server-Status unbekannt
      // ist (twoFactorEnabled === null), darf show NIE true werden — sonst flackert
      // das Modal beim Page-Load kurz auf bevor der /api/auth/2fa/status-Fetch
      // mit enabled:true antwortet. Race-Bedingung gefixt.
      if (twoFactorEnabled === null) { setShow(false); return }
      if (twoFactorEnabled === true) { setShow(false); return }
      // URL-Param ?require2fa=1 vom Middleware-Redirect: SessionDismiss+Snooze
      // cleart, Modal zwingend zeigen.
      let forcedByUrl = false
      try {
        const params = new URLSearchParams(window.location.search)
        if (params.get('require2fa') === '1') {
          try { sessionStorage.removeItem(SESSION_DISMISS_KEY) } catch {}
          try { localStorage.removeItem(SNOOZE_KEY) } catch {}
          forcedByUrl = true
        }
      } catch {}
      // SessionDismiss respektieren — auch fuer Admin (Modal-Spam-Stop!)
      if (!forcedByUrl) {
        try {
          if (sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') { setShow(false); return }
        } catch {}
      }
      if (isAdmin) { setShow(true); return }
      // Nicht-Admin: Snooze respektieren
      try {
        const snoozeUntil = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10)
        if (snoozeUntil > Date.now()) { setShow(false); return }
      } catch {}
      setShow(true)
    }

    recompute()

    // BFCache-Workaround: Browser-Back stellt Page aus Cache wieder her, ohne
    // useEffect erneut zu feuern. pageshow mit persisted=true ist genau dieser
    // Fall — wir muessen den State explizit neu berechnen.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) recompute()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [twoFactorEnabled, isAdmin])

  return { show, force: isAdmin }
}
